import { redirectAfterLogin, useLogin } from "@/hooks/data/useLogin"
import { usePlatform } from "@/platform/context"
import { loginSchema } from "@oblique/api/types"
import { useForm, useStore } from "@tanstack/react-form"
import { useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useSearchParams } from "react-router"
import { Button } from "../components/ui/button"
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../components/ui/card"
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../components/ui/form"
import { Input } from "../components/ui/input"
import { useAuth } from "../providers/auth"

export const Login = () => {
	const [searchParams] = useSearchParams()
	const location = useLocation()
	const platform = usePlatform()
	const locationState = location.state as {
		from?: { pathname?: string }
	} | null
	const navigate = useNavigate()
	const { status, getGoogleSignInUrl, user } = useAuth()

	const redirect =
		searchParams.get("redirect") ??
		locationState?.from?.pathname ??
		"/dashboard"

	useEffect(() => {
		if (status === "authenticated" && user) {
			void redirectAfterLogin(redirect, navigate).catch((error) => {
				console.error("Redirect after existing session failed:", error)
			})
		}
	}, [status, user, redirect, navigate])

	const [submitError, setSubmitError] = useState<string | null>(null)
	const loginMutation = useLogin(redirect)
	const autoLoginAttempted = useRef(false)

	const form = useForm({
		defaultValues: {
			email: platform.defaultEmail ?? "",
			password: platform.defaultPassword ?? "",
		},
		validators: {
			onSubmit: loginSchema,
		},
		onSubmit: async ({ value }) => {
			setSubmitError(null)

			try {
				await loginMutation.mutateAsync(value)
			} catch (error) {
				if (error instanceof Error && error.message) {
					setSubmitError(error.message)
				} else {
					setSubmitError("Something went wrong. Please try again.")
				}
			}
		},
	})

	const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
	const isBusy = isSubmitting || loginMutation.isPending

	useEffect(() => {
		if (autoLoginAttempted.current || platform.kind !== "electron") {
			return
		}
		if (!platform.defaultEmail || !platform.defaultPassword) {
			return
		}

		autoLoginAttempted.current = true
		void loginMutation.mutateAsync({
			email: platform.defaultEmail,
			password: platform.defaultPassword,
		})
	}, [
		loginMutation,
		platform.defaultEmail,
		platform.defaultPassword,
		platform.kind,
	])

	return (
		<div className="mx-auto flex w-full max-w-sm flex-col gap-6 p-6">
			<Card>
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl font-semibold">Sign in</CardTitle>
					<CardDescription>
						Enter your account credentials to reach your dashboard.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Form form={form}>
						<form
							className="space-y-6"
							onSubmit={(event) => {
								event.preventDefault()
								event.stopPropagation()
								void form.handleSubmit()
							}}
							noValidate
						>
							<FormField
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input
												name={field.name}
												value={(field.value as string) ?? ""}
												onBlur={field.onBlur}
												onChange={(event) => {
													setSubmitError(null)
													field.onChange(event)
												}}
												type="email"
												autoComplete="email"
												placeholder="you@example.com"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Password</FormLabel>
										<FormControl>
											<Input
												name={field.name}
												value={(field.value as string) ?? ""}
												onBlur={field.onBlur}
												onChange={(event) => {
													setSubmitError(null)
													field.onChange(event)
												}}
												type="password"
												autoComplete="current-password"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{submitError ? (
								<p className="text-sm font-medium text-destructive">
									{submitError}
								</p>
							) : null}

							<Button type="submit" className="w-full" disabled={isBusy}>
								{isBusy ? "Signing in…" : "Sign in"}
							</Button>
						</form>
					</Form>
				</CardContent>
				<CardFooter className="flex flex-col gap-4">
					{platform.kind === "web" ? (
						<>
							<Button
								type="button"
								variant="outline"
								className="w-full"
								disabled={isBusy}
								onClick={() => {
									const url = getGoogleSignInUrl(
										redirect !== "/dashboard" ? redirect : undefined,
									)
									window.location.href = url
								}}
							>
								Continue with Google
							</Button>
							<p className="text-center text-sm text-muted-foreground">
								Need an account?{" "}
								<Button variant="link" className="px-1" asChild>
									<Link
										to={`/register?redirect=${encodeURIComponent(redirect)}`}
									>
										Register instead
									</Link>
								</Button>
							</p>
						</>
					) : null}
				</CardFooter>
			</Card>
		</div>
	)
}
